import React, { useEffect, useReducer } from "react";
import "./Lightcast.scss";
import translate from "@/i18n/translate";
import { sendRequest } from "@/common/http";
import WidgetLoader from "../common/elements/WidgetLoader";
import "@maptiler/sdk/dist/maptiler-sdk.css";
import * as maptilersdk from "@maptiler/sdk";
import ApiError from "../common/elements/ApiError";
import { County, RegionalContext } from "./Interface";
export interface InstitutionRegionContextProps {
  regionalContext: RegionalContext | null;
  onComponentLoad: (component: string) => void;
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface Action {
  type: string;
  data: County[];
}

interface InstitutionRegionContextState {
  regionalPopulation: County[] | null;
  loader: boolean;
}

const initialState = {
  regionalPopulation: null,
  loader: true
};

const defaultAction = {
  type: "",
  data: []
};

const reducer = (state: InstitutionRegionContextState, action: Action) => {
  switch (action.type) {
    case "setPopulationByCounty":
      return { ...state, regionalPopulation: action.data, loader: false };
    case "setPopulationByCountyError":
      return { ...state, regionalPopulation: null, loader: false };
    default:
      return state;
  }
};
export default function InstitutionRegionContext({
  regionalContext,
  onComponentLoad
}: InstitutionRegionContextProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const applyResult = (result: County[]) => {
      dispatch({ type: "setPopulationByCounty", data: result });
      onComponentLoad("regionalContext");
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setPopulationByCountyError"
      });
      onComponentLoad("regionalContext");
    };

    sendRequest(
      { uri: "/lightcast/population_by_county", camelize: true },
      applyResult,
      onError
    );
  }, []);

  useEffect(() => {
    if (state.regionalPopulation && regionalContext) {
      generateRegionalMap(
        regionalContext.mapTilerApiKey,
        regionalContext.mapId,
        regionalContext.coordinates,
        state.regionalPopulation
      );
    }

    const regionalMapDiv = document.getElementById("regional-map");
    if (regionalMapDiv) {
      setTimeout(() => {
        regionalMapDiv.style.height = "460px";
      }, 2000);
    }
  }, [state.regionalPopulation, regionalContext]);
  return (
    <div className="grid-container wm-grid-col-12 wm-grid-col-lg-12 wm-grid-col-md-10 wm-grid-col-sm-8">
      <div className="paper-card">
        <div className="header">
          <div className="info">
            <h3 className="title">
              {translate("app.institutionRegionContext.indusRegionContext")}
            </h3>
            <div className="description">
              {translate("app.institutionRegionContext.reviewLastFive")}
            </div>
          </div>
        </div>
        <div className="body wm-grid">
          {!state.loader && regionalContext && state.regionalPopulation && (
            <Body
              regionalContext={regionalContext}
              regionalPopulation={state.regionalPopulation}
            />
          )}
          {state.loader && (
            <WidgetLoader
              loaderContainerClasses="loader"
              loaderClasses="content"
            />
          )}
          {!state.loader && (!regionalContext || !state.regionalPopulation) && (
            <ApiError />
          )}
        </div>
      </div>
    </div>
  );
}

const Body = (props: any) => {
  const regionalPopulation = props.regionalPopulation.filter((rp: any) => {
    return rp.name !== "Total";
  });

  const totalRegionalPopulation = props.regionalPopulation.filter((rp: any) => {
    return rp.name === "Total";
  });

  return (
    <div className="">
      <div className="institution-detail base-font-size">
        {translate("app.lightcast.institutionDetails")}
      </div>
      <div className="body context-widget-labor-force">
        <div className="context-ipeds -detail">
          <div className="-detail-value">
            <div className="context-count">{props.regionalContext?.zip}</div>
            <div className="context-label">
              {translate("app.institutionRegionContext.institutionIPEDS")}
            </div>
          </div>
        </div>
        <div className="context-detail -detail">
          <div className="-detail-value">
            <div className="context-count">
              {props.regionalContext?.radius} mi
            </div>
            <div className="context-label">
              {translate("app.institutionRegionContext.radiusZipCode")}
            </div>
          </div>
        </div>
        <div className="context-detail -detail context-time-frame">
          <div className="-detail-value">
            <div className="context-count">
              {props.regionalContext?.timeFrame}
            </div>
            <div className="context-label">
              {translate("app.institutionRegionContext.fiveYear")}
            </div>
          </div>
        </div>
        <div className="context-counties">
          <div className="-detail-value">
            <div className="context-count">
              {props.regionalContext?.noOfCounties}
            </div>
            <div className="context-label">
              {translate("app.institutionRegionContext.countiesIncluded")}
            </div>
          </div>
        </div>
      </div>
      <div className="flex-row map-container">
        <div className="map-wrap">
          <div className="-header county-map-header base-font-size">
            {translate("app.lightcast.mapByCounty")}
          </div>
          <div id="regional-map" className="regional-map"></div>
          <div id="legend" className="overlay">
            <h4 className="label-heading">
              {translate("app.lightcast.countyByPopulation")}
            </h4>
            <div className="labels">
              <div className="flex-row -verticallycentered percentage-container">
                <span className="zero-percentage percentage-box"></span>
                {translate("app.lightcast.zero")}
              </div>
              <div className="flex-row -verticallycentered percentage-container">
                <span className="twenty-percentage percentage-box"></span>
                {translate("app.lightcast.twenty")}
              </div>
              <div className="flex-row -verticallycentered percentage-container">
                <span className="fourty-percentage percentage-box"></span>
                {translate("app.lightcast.fourty")}
              </div>
              <div className="flex-row -verticallycentered percentage-container">
                <span className="sixty-percentage percentage-box"></span>
                {translate("app.lightcast.sixty")}
              </div>
              <div className="flex-row -verticallycentered percentage-container">
                <span className="eighty-percentage percentage-box"></span>
                {translate("app.lightcast.eighty")}
              </div>
            </div>
          </div>
        </div>
        <div className="-context">
          <div className="-header county-map-header base-font-size">
            {translate("app.institutionRegionContext.regionalPopulation")}
          </div>
          <div className="table-container">
            <table className="data-table -shadow county-table county-table-labor-market">
              <thead className="dataheader">
                <tr className="dataheaderrow">
                  <th className="dataheadercell" data-th="County">
                    {translate("app.institutionRegionContext.county")}
                  </th>
                  <th className="dataheadercell" data-th="Population">
                    {translate("app.institutionRegionContext.population")}
                  </th>
                  <th className="dataheadercell" data-th="Percentage">
                    {translate("app.institutionRegionContext.percentage")}
                  </th>
                </tr>
              </thead>
              <tbody
                className="databody county-table-body"
                tabIndex={0}
              >
                {props.regionalPopulation &&
                  regionalPopulation.map((rp: any) => {
                    return (
                      <tr className="datarow" key={rp?.name}>
                        <td className="datacell">{rp?.name}</td>
                        <td className="datacell">{rp?.population}</td>
                        <td className="datacell">{rp?.percentage}%</td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot className="footer-border">
                {props.regionalPopulation &&
                  totalRegionalPopulation.map((rp: any) => {
                    return (
                      <tr className="datarow" key={rp?.name}>
                        <td className="datacell">
                          {translate("app.institutionRegionContext.total")}
                        </td>
                        <td className="datacell">{rp?.population}</td>
                        <td className="datacell">{rp?.percentage}%</td>
                      </tr>
                    );
                  })}
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const generateRegionalMap = async (
  apiKey: string,
  mapId: string,
  coordinates: Coordinates,
  regionalPopulation: Array<County>
) => {
  const mapContainer = document.getElementById("regional-map") as HTMLElement;
  const map = new maptilersdk.Map({
    container: mapContainer,
    style: `https://api.maptiler.com/maps/${mapId}/style.json`,
    center: [coordinates.longitude, coordinates.latitude],
    zoom: 5.5,
    apiKey: apiKey
  });

  await map.onLoadAsync();

  map.addSource("statesData", {
    type: "vector",
    url: "https://api.maptiler.com/tiles/countries/tiles.json"
  });

  const layers = map.getStyle().layers;
  let firstSymbolId;
  for (let i = 0; i < layers.length; i++) {
    if (layers[i].type === "symbol") {
      firstSymbolId = layers[i].id;
      break;
    }
  }

  addCountiesLayer(map, firstSymbolId);

  const vizData: Map<string, County> = new Map(
    regionalPopulation.map((county: County) => [county.code, county])
  );

  function setStates() {
    const counties = map.querySourceFeatures("statesData", {
      sourceLayer: "administrative",
      filter: ["==", "level", 2]
    });

    counties.forEach((county) => {
      let code: string = county.properties.code;
      if (county.id && vizData.get(code)) {
        map.setFeatureState(
          {
            source: "statesData",
            sourceLayer: "administrative",
            id: county.id
          },
          {
            percentage: vizData.get(code)?.percentage
          }
        );
      }
    });

    if (counties.length !== 0) {
      map.off("data", afterLoad);
    }
  }

  map.on("click", "counties", function (e: any) {
    const code: string = e.features[0].properties.code;
    const county: County = vizData.get(code) as County;
    county &&
      new maptilersdk.Popup()
        .setLngLat(e.lngLat)
        .setHTML(
          `<h3 class="red">${county.population}</h3><p>${county.name}</p>`
        )
        .addTo(map);
  });

  function afterLoad() {
    if (map.getSource("statesData") && map.isSourceLoaded("statesData")) {
      setStates();
    }
  }
  if (map.isSourceLoaded("statesData")) {
    setStates();
  } else {
    map.on("sourcedata", afterLoad);
  }

  map.on("mousemove", "counties", function (e: any) {
    const code: string = e.features[0].properties.code;
    const county: County = vizData.get(code) as County;
    county
      ? (map.getCanvas().style.cursor = "pointer")
      : (map.getCanvas().style.cursor = "default");
  });

  map.on("sourcedata", function () {
    map.getCanvas().style.cursor = "default";
  });
};

const addCountiesLayer = (map: any, firstSymbolId: any) => {
  map.addLayer(
    {
      id: "counties",
      type: "fill",
      source: "statesData",
      "source-layer": "administrative",
      filter: ["==", "level", 2],
      paint: {
        "fill-color": [
          "case",
          ["!=", ["to-number", ["feature-state", "percentage"]], 0],
          [
            "step",
            ["feature-state", "percentage"],
            "#E5ECF5",
            20,
            "#A4BDDE",
            40,
            "#3C76BB",
            60,
            "#275182",
            80,
            "#122A47"
          ],
          "rgba(0, 0, 0, 0)"
        ],
        "fill-opacity": 1,
        "fill-outline-color": "#000"
      }
    },
    firstSymbolId
  );
};
